import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal, viewChild } from '@angular/core';
import { FormErrorComponent } from '../../form-error/form-error.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { TypeaheadInputComponent } from '../../inputs/typeahead-input/typeahead-input.component';
import { debounceTime, filter, Observable, of, startWith, Subscription, switchMap } from 'rxjs';
import { InputSize } from '../../../enum/input-size.enum';
import { indicateSignal } from '../../../../extensions/observable.extensions';

@Component({
  selector: 'lib-typeahead-form-field',
  imports: [FormErrorComponent, TypeaheadInputComponent, ReactiveFormsModule],
  templateUrl: './typeahead-form-field.component.html',
  styleUrl: './typeahead-form-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TypeaheadFormFieldComponent {
  inputFormControl = input.required<FormControl>();
  minCharactersToStart = input<number>(2);
  debounceTime = input<number>(200);
  label = input.required<string>();
  isSubmitted = input<boolean>(false);
  hintText = input<string | null>();
  placeholder = input<string | null | undefined>();
  initialSelectedName = input<string | null>();
  fetchResultsFn = input<(searchText: string) => Observable<TypeaheadOption[] | null> | null | undefined>();

  inputId: string = uuidv4();
  textInputFormControl = new FormControl<string>('');
  loadingResults = signal<boolean>(false);
  currentResults = signal<TypeaheadOption[] | null>(null);
  resultStrings = computed(() => {
    return this.currentResults()?.map(option => option.name) ?? null;
  });
  selectedResult = signal<TypeaheadOption | null>(null);
  inputFormControlValue = signal<unknown>(null);
  typeaheadInputComponent = viewChild<TypeaheadInputComponent>('typeaheadInputComponent');
  currentResultSelected = output<TypeaheadOption | null>();
  private subs: Subscription[] = [];

  filteredInitialSelectedName = computed(() => {
    if (this.inputFormControlValue() && !this.selectedResult()) {
      return this.initialSelectedName();
    } else {
      return null;
    }
  });

  formControlEffect = effect(() => {
    const newInputFormControl = this.inputFormControl();

    this.subs.forEach(sub => sub.unsubscribe());
    this.subs.push(
      newInputFormControl.statusChanges.pipe(startWith(newInputFormControl.status)).subscribe(status => {
        if (status === 'DISABLED') {
          this.textInputFormControl.disable();
        } else {
          this.textInputFormControl.enable();
        }
      }),
      newInputFormControl.valueChanges.pipe(startWith(newInputFormControl.value)).subscribe(value => {
        this.inputFormControlValue.set(value);
        if (value === null && this.selectedResult()) {
          this.typeaheadInputComponent()?.clear();
        }
      }),
    );
  });

  private listenToFormControl = this.textInputFormControl.valueChanges
    .pipe(
      debounceTime(this.debounceTime()),
      filter(() => {
        return !this.selectedResult();
      }),
      switchMap(v => {
        if (v === null || v.length < this.minCharactersToStart()) {
          // Hide results if the user has not entered enough characters
          return of(null);
        }
        if (this.fetchResultsFn()) {
          const fetchFn = this.fetchResultsFn() as (v: string) => Observable<TypeaheadOption[]>;
          return fetchFn(v).pipe(indicateSignal(this.loadingResults));
        } else {
          return of(null);
        }
      }),
    )
    .subscribe({
      next: value => {
        this.currentResults.set(value);
      },
      error: () => {
        this.inputFormControl().setErrors({ invalid: $localize`Could not fetch results` });
      },
    });

  protected readonly InputSize = InputSize;

  resultIndexSelected(index: number) {
    const result = this.currentResults()?.[index];
    this.selectedResult.set(result ?? null);
    this.textInputFormControl.setValue(result?.name ?? '');
    this.inputFormControl().setValue(result?.value ?? null);
    this.currentResultSelected.emit(result ?? null);
  }

  valueChange(value: string | null) {
    if (value === null) {
      this.selectedResult.set(null);
      this.textInputFormControl.setValue('');
      this.inputFormControl().setValue(null);
    }
  }
}

export interface TypeaheadOption {
  name: string;
  value: string | number;
}
